// Lightweight RPC client su WebSocket. Speculare a server/src/signaling.ts.

type Pending = { resolve: (v: unknown) => void; reject: (e: Error) => void };
type Notification = { type: string; [k: string]: unknown };

export class SignalingClient {
  private ws: WebSocket;
  private pending = new Map<string, Pending>();
  private notificationHandlers = new Map<string, (n: Notification) => void>();
  private ready: Promise<void>;
  private closeHandlers: Array<() => void> = [];

  constructor(url: string) {
    this.ws = new WebSocket(url);
    this.ready = new Promise((resolve, reject) => {
      this.ws.onopen = () => resolve();
      this.ws.onerror = (e) => reject(new Error(`WS error: ${(e as Event).type}`));
    });
    this.ws.onmessage = (ev) => this.onMessage(ev.data);
    this.ws.onclose = () => {
      for (const p of this.pending.values()) p.reject(new Error('WS closed'));
      this.pending.clear();
      for (const h of this.closeHandlers) try { h(); } catch { /* ignore */ }
    };
  }

  /** true se la WebSocket è ancora aperta. */
  isOpen(): boolean { return this.ws.readyState === WebSocket.OPEN; }

  /** Registra una callback chiamata quando la WS si chiude. */
  onClose(handler: () => void): void { this.closeHandlers.push(handler); }

  awaitReady() {
    return this.ready;
  }

  on(notificationType: string, handler: (n: Notification) => void) {
    this.notificationHandlers.set(notificationType, handler);
  }

  request<T = unknown>(request: object): Promise<T> {
    const id = crypto.randomUUID();
    return new Promise<T>((resolve, reject) => {
      this.pending.set(id, { resolve: resolve as (v: unknown) => void, reject });
      this.ws.send(JSON.stringify({ id, request }));
    });
  }

  close() {
    this.ws.close();
  }

  private onMessage(raw: string) {
    let msg: { id?: string; ok?: boolean; data?: unknown; error?: string; type?: string };
    try {
      msg = JSON.parse(raw);
    } catch {
      return;
    }
    // Notifiche server: identificate dal campo `type`. Possono contenere anche `id`
    // (es. recordingStarted con id della registrazione) → controllo type PRIMA di id.
    if (msg.type) {
      const h = this.notificationHandlers.get(msg.type);
      if (h) h(msg as Notification);
      return;
    }
    // RPC response: ha `id` (echo del request id) ma niente `type`.
    if (msg.id) {
      const p = this.pending.get(msg.id);
      if (!p) return;
      this.pending.delete(msg.id);
      if (msg.ok) p.resolve(msg.data);
      else p.reject(new Error(msg.error ?? 'rpc error'));
    }
  }
}
